import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import api from '../../utils/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const AdminOrders = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [location, setLocation] = useState({ lat: '', lng: '' });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchOrders();
  }, [user, navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/admin/orders/${orderId}/status?status=${newStatus}`);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;

    try {
      const locationData = {
        lat: parseFloat(location.lat),
        lng: parseFloat(location.lng)
      };
      await api.put(`/admin/orders/${selectedOrder.id}/location`, locationData);
      toast.success('Delivery location updated');
      setShowLocationDialog(false);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update location');
    }
  };

  const openLocationDialog = (order) => {
    setSelectedOrder(order);
    if (order.current_location) {
      setLocation({
        lat: order.current_location.lat.toString(),
        lng: order.current_location.lng.toString()
      });
    } else {
      setLocation({ lat: '19.0760', lng: '72.8777' });
    }
    setShowLocationDialog(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-cyan-100 text-cyan-800',
      in_transit: 'bg-yellow-100 text-yellow-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-primary text-white border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="font-heading text-2xl font-bold uppercase">Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm">{user.name}</span>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-white text-white hover:bg-white hover:text-primary">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link to="/admin" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Dashboard
            </Link>
            <Link to="/admin/products" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Products
            </Link>
            <Link to="/admin/categories" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Categories
            </Link>
            <Link to="/admin/orders" className="text-sm font-medium text-secondary border-b-2 border-secondary pb-1">
              Orders
            </Link>
            <Link to="/admin/shop-info" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Shop Info
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="font-heading text-3xl font-bold text-primary mb-6 uppercase" data-testid="orders-management-heading">Orders Management</h2>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-sm shadow-sm p-12 text-center">
            <p className="text-slate-600">No orders yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-sm shadow-sm overflow-hidden" data-testid="orders-table">
            <table className="w-full">
              <thead className="bg-slate-100 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Order ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Items</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {orders.map((order) => (
                  <tr key={order.id} data-testid={`order-row-${order.id}`}>
                    <td className="px-6 py-4">
                      <p className="font-mono text-sm font-medium text-primary">{order.id.slice(0, 8)}</p>
                      <p className="text-xs text-slate-500">{new Date(order.created_at).toLocaleDateString()}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium">{order.delivery_info.name}</p>
                      <p className="text-xs text-slate-500">{order.delivery_info.phone}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{order.items.length} items</td>
                    <td className="px-6 py-4 text-sm font-medium">₹{order.total.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <Select
                        value={order.order_status}
                        onValueChange={(value) => updateOrderStatus(order.id, value)}
                      >
                        <SelectTrigger className="w-32" data-testid={`order-status-select-${order.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="placed">Placed</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="in_transit">In Transit</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openLocationDialog(order)}
                        data-testid={`update-location-${order.id}`}
                      >
                        <MapPin className="h-4 w-4 mr-1" />
                        Update Location
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl uppercase">Update Delivery Location</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateLocation} className="space-y-4">
              <div>
                <Label htmlFor="lat">Latitude *</Label>
                <Input
                  id="lat"
                  type="number"
                  step="any"
                  value={location.lat}
                  onChange={(e) => setLocation({ ...location, lat: e.target.value })}
                  required
                  data-testid="location-lat-input"
                />
              </div>
              <div>
                <Label htmlFor="lng">Longitude *</Label>
                <Input
                  id="lng"
                  type="number"
                  step="any"
                  value={location.lng}
                  onChange={(e) => setLocation({ ...location, lng: e.target.value })}
                  required
                  data-testid="location-lng-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="save-location-button">
                  Update Location
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowLocationDialog(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminOrders;